/**
 * Leads: people who asked about OSP or MyWalkThru and aren't a deal yet (design: "zCRM Leads
 * UX"; research: "How leads arrive today"). A lead is a Prospect task, so it is writable on the
 * live API. A lead is answered within one business day, then converted to a deal (a Sales task
 * at "New lead") or disqualified with a reason.
 *
 * Where each lead field is stored on the Prospect task (`proposed` = a spare task field that
 * needs the team's OK; see zcrm-docs/reference/leads-module.md):
 *
 * | Lead field          | Task field                  |          | Long-term home             |
 * |---------------------|-----------------------------|----------|----------------------------|
 * | company             | Description (or Property)   |          | Property.Name              |
 * | person              | TenantNames                 |          | Contact.FirstName/LastName |
 * | email               | Email                       |          | Contact.Email              |
 * | mobile · office     | PhoneNumber ("mobile · office") |      | Contact.MobilePhone/Office |
 * | type                | EntranceInstruction         | proposed | Property.AccountType       |
 * | doors               | AuthorizedLimit             | proposed | Property.ServiceableDoors  |
 * | product             | Key (as on deals)           |          | Lease.Product              |
 * | request             | WorkOrderInstructions       |          | deal notes                 |
 * | source              | EntranceInstructionDesc     | proposed | Property.LeadSource        |
 * | heard, referred by, | PetsDesc ("heard|referred|markets") | proposed | Property.LeadSource / ReferredBy / UserGroup |
 * |   markets           |                             |          |                            |
 * | chat link           | Notes                       | proposed | Lease.SourceURL            |
 * | booked at a booth   | Pets (as on deals)          |          |                            |
 * | marketing consent   | IsBidOnly                   | proposed | Contact.Is1099Required     |
 * | owner               | ManagedBy                   |          | Property.AccountManager    |
 * | reply due           | EndDate                     |          |                            |
 * | replied at          | StartDate                   |          |                            |
 * | next step / reason  | KeyDesc (next step while open, the reason once disqualified)  |
 */

/** Lead statuses, stored as Task.Status on the Prospect task. */
export const LEAD_STATUSES = [
  { id: "new", label: "New", status: "New" },
  { id: "contacted", label: "Contacted", status: "In Progress" },
  { id: "qualified", label: "Qualified", status: "Closed" },
  { id: "disqualified", label: "Disqualified", status: "Canceled" },
] as const;
export type LeadStatusId = (typeof LEAD_STATUSES)[number]["id"];

/** Where a lead came in. The door sets it; nobody types it. */
export const LEAD_SOURCES = [
  { id: "chat_osp", label: "Chat · OSP", group: "chat" },
  { id: "chat_mwt", label: "Chat · MWT", group: "chat" },
  { id: "web_form", label: "Web form", group: "form" },
  { id: "call_centre", label: "Call centre", group: "call" },
  { id: "booth", label: "Booth", group: "booth" },
  { id: "mobile", label: "Mobile quick add", group: "booth" },
] as const;
export type LeadSourceId = (typeof LEAD_SOURCES)[number]["id"];

/** The inbox's source chips. */
export const LEAD_SOURCE_GROUPS = [
  { id: "chat", label: "Chat" },
  { id: "form", label: "Web form" },
  { id: "call", label: "Call centre" },
  { id: "booth", label: "Booth / mobile" },
] as const;

/** "How did you hear about us?": required, not captured anywhere in Pipedrive today. */
export const HOW_HEARD = [
  "Google search",
  "Website chat",
  "NARPM or another event",
  "Realtor or association meeting",
  "Referral",
  "Existing relationship",
  "Other",
] as const;

/** Mobile quick add: "Where did we meet?" */
export const WHERE_MET = [
  "NARPM or another event",
  "Realtor or association meeting",
  "Referral",
  "Other",
] as const;

/** What happens after the first call. */
export const LEAD_NEXT_STEPS = [
  "Book a demo",
  "Send pricing by email",
  "Call back",
] as const;

/** Disqualifying needs one of these. */
export const DISQUALIFY_REASONS = [
  "Not in our market (waitlist)",
  "Too small for us",
  "Support request, not a lead",
  "No way to reach them",
  "Spam",
  "Duplicate of another lead",
  "Other",
] as const;

/** The market a lead picks when we don't serve its city yet: it goes to the market waitlist. */
export const WAITLIST_MARKET = "Other (waitlist)";

/** Every new lead gets a reply within this many business days. */
export const FIRST_REPLY_BUSINESS_DAYS = 1;
/** A reply due within this many hours shows amber. */
export const REPLY_SOON_HOURS = 8;

/**
 * Email domains shared by many people: two leads on gmail.com are not the same company, so these
 * never count as a company match.
 */
export const PUBLIC_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "aol.com",
  "live.com",
  "msn.com",
  "me.com",
  "comcast.net",
  "att.net",
  "example.com",
] as const;

/** Words that make a request look like a resident asking for app help (it goes to Help desk). */
export const SUPPORT_REQUEST_WORDS = [
  "log in",
  "login",
  "password",
  "code never",
  "app help",
  "can't open",
  "cannot finish",
  "my inspection",
  "my deposit",
] as const;
